import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye, Shield, User, KeyRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar'
import { getTimezoneInfo } from '@/utils/dynamicTimezone'
import { useState } from 'react'
import type { User as UserType } from '@/types/entities'
import { LANGUAGE_NAMES, LANGUAGE_FLAGS } from '@/lib/constants/languages'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { UserNameWithPresence } from '@/components/ui/UserNameWithPresence'
import { getResponsiveColumnSize } from '@/lib/utils/responsive-columns'

// Helper function to check if user is an administrator
function isAdministrator(user: UserType): boolean {
    return user.roles?.some(role => role.name === 'admin') || false
}

interface UserActionsProps {
    user: UserType
    onEdit: (user: UserType) => void
    onDelete: (user: UserType) => void
    onView: (user: UserType) => void
    onChangePassword?: (user: UserType) => void
}

function UserActions({ user, onEdit, onDelete, onView, onChangePassword }: UserActionsProps) {
    const { t } = useTranslation()
    const isAdmin = isAdministrator(user)
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                // Prevent table row click handlers
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                // Prevent table focus changes
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">{t('users.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                    onCloseAutoFocus={(e) => {
                        // Prevent focus from jumping back into table
                        e.preventDefault()
                    }}
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onView(user)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('users.viewUser')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(user)
                            setOpen(false)
                        }}
                    >
                        <Edit className="mr-2 h-4 w-4" />
                        {t('users.editUser')}
                    </DropdownMenuItem>
                    {onChangePassword && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onChangePassword(user)
                                setOpen(false)
                            }}
                        >
                            <KeyRound className="mr-2 h-4 w-4" />
                            {t('userForm.changePassword')}
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(user)
                            setOpen(false)
                        }}
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('users.deleteUser')}
                        {isAdmin && (
                            <span className="ml-2 text-xs text-muted-foreground">
                                ({t('users.admin')})
                            </span>
                        )}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

export function createUserColumns(
    onEdit: (user: UserType) => void,
    onDelete: (user: UserType) => void,
    onView: (user: UserType) => void,
    t: (key: string) => string,
    formatDate?: (date: string) => string,
    onChangePassword?: (user: UserType) => void
): ColumnDef<UserType>[] {
    // Calculate responsive column sizes
    const columnSizes = {
        name: getResponsiveColumnSize(250),
        roles: getResponsiveColumnSize(180),
        language: getResponsiveColumnSize(150),
        timezone: getResponsiveColumnSize(180),
        status: getResponsiveColumnSize(100),
        mfa: getResponsiveColumnSize(100),
        createdAt: getResponsiveColumnSize(130),
        actions: 80  // Fixed width
    }

    return [
        {
            accessorKey: 'name',
            header: t('users.user'),
            size: columnSizes.name,
            minSize: 200,
            maxSize: 400,
            cell: ({ row }) => {
                const user = row.original
                const isSystemUser = (user as any).is_system || (user as any).isSystem

                return (
                    <div className="flex items-center space-x-3">
                        <EnhancedAvatar
                            name={user.name}
                            avatarUrl={user.avatarUrl}
                            avatarColor={user.avatarColor}
                            userId={user.id}
                            size="sm"
                        />
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium"><UserNameWithPresence userId={user.id} name={user.name} /></span>
                                {isSystemUser && (
                                    <Badge variant="destructive" className="text-xs">
                                        <Shield className="h-3 w-3 mr-1" />
                                        {t('users.system')}
                                    </Badge>
                                )}
                            </div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                    </div>
                )
            },
        },
        {
            accessorKey: 'roles',
            header: t('users.roles'),
            size: columnSizes.roles,
            minSize: 150,
            maxSize: 280,
            cell: ({ row }) => {
                const roles = row.getValue('roles') as UserType['roles']
                return (
                    <div className="flex flex-wrap gap-1">
                        {roles.map((role) => (
                            <Badge
                                key={role.id}
                                variant="info"
                                className="text-xs"
                            >
                                {role.name === 'admin' ? (
                                    <Shield className="mr-1 h-3 w-3" />
                                ) : (
                                    <User className="mr-1 h-3 w-3" />
                                )}
                                {role.name === 'admin' || role.name === 'user' || role.name === 'viewer' ? t(`roles.${role.name}`) : <TranslatableText text={role.name} />}
                            </Badge>
                        ))}
                    </div>
                )
            },
        },
        {
            id: 'teams',
            header: t('users.teams'),
            size: 60,
            minSize: 50,
            maxSize: 70,
            cell: ({ row }) => {
                const user = row.original
                const teams = user.teams || []
                if (teams.length === 0) {
                    return <div className="flex items-center justify-center">-</div>
                }
                return (
                    <div className="flex items-center justify-center text-sm text-muted-foreground">
                        {teams.length}
                    </div>
                )
            },
        },
        {
            accessorKey: 'language',
            header: t('users.language'),
            size: columnSizes.language,
            minSize: 120,
            maxSize: 220,
            cell: ({ row }) => {
                // Get the user's stored language preference from the database
                const userLanguage = row.getValue('language') as string || 'en-US'

                // Get language name with simple fallback
                const displayName = LANGUAGE_NAMES[userLanguage] || userLanguage

                return (
                    <div className="text-sm flex items-center space-x-2">
                        <span>{LANGUAGE_FLAGS[userLanguage] || '🇺🇸'}</span>
                        <span>{displayName || 'English'}</span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'timezone',
            header: t('users.timezone'),
            size: columnSizes.timezone,
            minSize: 150,
            maxSize: 280,
            cell: ({ row }) => {
                const timezone = row.getValue('timezone') as string
                const timezoneInfo = getTimezoneInfo(timezone || 'UTC')
                return (
                    <div className="text-sm">
                        <div className="font-medium">{timezone || 'UTC'}</div>
                        {timezoneInfo && (
                            <div className="text-xs text-muted-foreground">
                                {timezoneInfo.currentOffset}
                            </div>
                        )}
                    </div>
                )
            },
        },
        {
            accessorKey: 'isActive',
            header: t('users.status'),
            size: columnSizes.status,
            minSize: 80,
            maxSize: 150,
            cell: ({ row }) => {
                const isActive = row.getValue('isActive') as boolean
                return (
                    <Badge variant={isActive ? 'success' : 'danger'}>
                        {isActive ? t('users.active') : t('users.inactive')}
                    </Badge>
                )
            },
        },
        {
            accessorKey: 'mfa',
            header: t('users.mfa'),
            size: columnSizes.mfa,
            minSize: 80,
            maxSize: 150,
            cell: ({ row }) => {
                const mfa = row.getValue('mfa') as UserType['mfa']
                const hasMfa = mfa?.totpEnabled || mfa?.webauthnEnabled
                return (
                    <Badge variant={hasMfa ? 'success' : 'danger'}>
                        {hasMfa ? t('users.enabled') : t('users.disabled')}
                    </Badge>
                )
            },
        },
        {
            accessorKey: 'createdAt',
            header: t('users.created'),
            size: columnSizes.createdAt,
            minSize: 110,
            maxSize: 200,
            cell: ({ row }) => {
                const date = row.getValue('createdAt') as string
                return (
                    <div className="text-sm text-muted-foreground">
                        {formatDate ? formatDate(date) : new Date(date).toLocaleDateString()}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            size: columnSizes.actions,
            minSize: 80,
            maxSize: 80,
            header: () => (
                <div className="text-center w-full">
                    {t('common.actions')}
                </div>
            ),
            cell: ({ row }) => {
                const user = row.original
                return (
                    <UserActions
                        user={user}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onView={onView}
                        onChangePassword={onChangePassword}
                    />
                )
            },
        },
    ]
}



